// 3rd party
import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatExpansionPanel, MatTabGroup } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { Subscription } from 'rxjs/Rx';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';

// components
import { AppComponent } from 'app/app.component';
import { LayoutMainComponent } from 'app/shared/layout/layout-main/layout-main.component';
import { CalibrationListComponent } from 'app/calibrations/calibration-list/calibration-list.component';

// helpers
import { Helper } from 'app/shared/helpers/helper';

// models
import { Calibration } from 'app/calibrations/shared/calibration.model';
import { CriterionAcceptance } from 'app/shared/models/criterion-acceptance.model';
import { CalRegTable } from 'app/calibrations/shared/cal-reg-table.model';
import { EReferenceModeType } from 'app/shared/models/cal-criterion.model';
import { ERoleActionWebview, ContactCustomer } from 'app/shared/models/contact-customer.model';
import { StoredFile } from 'app/shared/models/stored-file.model';

// services
import { CalibrationService } from 'app/calibrations/shared/calibration.service';
import { CriterionService } from 'app/shared/services/criterion.service';
import { DialogsService } from 'app/shared/services/dialog/dialog.service';
import { InstrumentService } from 'app/instruments/shared/instrument.service';
import { StoredFileService } from 'app/shared/services/stored-file.service';
import { SupplementService } from 'app/calibrations/shared/supplement.service';

@Component({
  selector: 'app-calibration',
  templateUrl: './calibration.component.html',
  styleUrls: ['./calibration.component.css']
})
export class CalibrationComponent implements OnInit, OnDestroy {

  @ViewChild('calibrationPanel') calibrationPanel: MatExpansionPanel;
  @ViewChild('matTabGroup') matTabGroup: MatTabGroup;
  @ViewChild('pdfViewer') pdfViewer: PdfJsViewerComponent;

  attachments: any[] = new Array<any>();
  calibration: Calibration = new Calibration();
  checkedAttachments: any;
  checkedSupplements: any;
  chkAllAttachments: boolean = false;
  chkAllSupplements: boolean = false;
  columnTag: string;
  criteria: any[] = new Array<any>();
  hasCertificatesDownloadPermission: boolean = true;
  hasCertificatesViewPermission: boolean = true;
  hasChangeStatusPermission: boolean = true;
  isLoading: boolean;
  ReferenceModeType: typeof EReferenceModeType = EReferenceModeType;
  subscribe1: Subscription;
  supplements: any[] = new Array<any>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private app: AppComponent,
    public calibrationListComponent: CalibrationListComponent,
    public calibrationService: CalibrationService,
    public criterionService: CriterionService,
    public dialog: DialogsService,
    public instrumentService: InstrumentService,
    public mainLayout: LayoutMainComponent,
    public router: Router,
    public storedFileService: StoredFileService,
    public supplementService: SupplementService,
    public translate: TranslateService
  ) { }

  ngOnInit() {
    this.columnTag = localStorage.getItem('newCalibrationTagName');
    this.calibrationPanel.expanded = true;

    this.subscribe1 = this.activatedRoute.params.subscribe(params => {
      let id = params['identifier'];
      if (!id) {
        return;
      }
      this.calibrationListComponent.sidenavRight.open();

      this.calibrationService.get(id).subscribe(res => {
        this.calibration = Helper.deserialize(res.text())[0];
        this.loadAttachments();
        this.loadSupplements();
        if (this.matTabGroup && this.matTabGroup.selectedIndex === 1) {
          this.loadCriteria();
        }
        this.loadBands();
      });
    });
    this.checkPermissions();
  }

  ngOnDestroy() {
    if (this.subscribe1) {
      this.subscribe1.unsubscribe();
    }
    this.calibrationListComponent.sidenavRight.close();
  }

  checkAllAttachments(e) {
    this.attachments.forEach((item) => {
      item.checked = e.source.checked;
    });
    this.checkedAttachments = this.attachments.filter(item => item.checked).length;
  }

  checkAllSupplements(e) {
    this.supplements.forEach((item) => {
      item.checked = e.source.checked;
    });
    this.checkedSupplements = this.supplements.filter(item => item.checked).length;
  }

  checkPermissions() {
    const contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;
    this.hasChangeStatusPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesChangeStatus, contact.RoleWebView.ActionsPermissions);
    this.hasCertificatesDownloadPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesDownload, contact.RoleWebView.ActionsPermissions);
    this.hasCertificatesViewPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesView, contact.RoleWebView.ActionsPermissions);
  }

  close() {
    this.calibrationListComponent.sidenavRight.close();
    this.router.navigate([`/${this.app.currentContact.Domain}/calibrations/`]);
  }

  downloadCal() {
    this.isLoading = true;
    let params = [];
    params[0] = {
      identifier: this.calibration.Identifier,
      tableName: this.calibration.TableName
    };

    this.calibrationService.download(params)
      .subscribe(data => {
        this.isLoading = false;
        if (data) {
          FileSaver.saveAs(data, `${this.calibration.Code}.cal`);
        } else {
          this.dialog.error('Message.ErrorAlert', 'Message.DownloadCertificateError');
        }
      }, () => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.DownloadCertificateError');
      });
  }

  downloadAttachment() {
    this.isLoading = true;
    let lst = this.attachments.filter(at => at.checked);
    let files: any[] = new Array<any>();
    let fileName: string = '';

    lst.forEach(element => {
      (fileName = this.storedFileService.formatUserFileName(element.UserFileName));
      files.push({ identifier: element.Identifier, file: fileName ? fileName : element.SystemFileName, size: this.storedFileService.formatFileSize(element.size), systemFileName: element.SystemFileName, userFileName: element.UserFileName });
    });

    this.storedFileService.download(files)
      .subscribe(data => {
        if (data) {
          let fileName = files.length === 1 ? files[0].file : this.calibration.Code;
          FileSaver.saveAs(data, fileName);
          this.isLoading = false;
        } else {
          this.isLoading = false;
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
        }
      }, err => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
      });
  }

  downloadSupplement() {
    this.isLoading = true;
    let identifiers = this.supplements.filter(item => item.checked).map(v => v.Identifier);
    this.supplementService.download(identifiers).subscribe(data => {
      FileSaver.saveAs(data, this.calibration.Code);
      this.isLoading = false;
    }, err => {
      this.dialog.error('Message.ErrorAlert', 'Message.DownloadCertificateSupplementError');
      this.isLoading = false;
    });
  }

  fillBand(band) {
    this.translate.get('Translate.BandsAmplitude').subscribe((val) => {
      band.BandDescription = Helper.getRangeValueFormated(band.Name, band.MinValue, band.MaxValue, band.Precision, band.MeasurementUnit ? band.MeasurementUnit.Abbreviation : null, val);
    });
  }

  getAttachmentName(sf: StoredFile): string {
    let name = this.storedFileService.formatUserFileName(sf.UserFileName);
    return name ? name : sf.SystemFileName;
  }

  getSituationDescription(status: number) {
    return this.calibrationService.getSituationDescription(status);
  }

  getSituationIcon(status: number) {
    return this.calibrationService.getSituationIcon(status);
  }

  getStatusIcon(status: number) {
    return this.calibrationService.getStatusIcon(status);
  }

  loadAttachments() {
    this.isLoading = true;
    let lstFilters = '"Calibration.Identifier_=\'' + this.calibration.Identifier + '\'","IsAttach"';
    let lstFields = '"SystemFileName","UserFileName","Size"';

    this.storedFileService
      .get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(values => {
        this.isLoading = false;
        this.attachments = values.json();

        if (this.attachments.length > 0) {
          this.attachments.forEach(element => {
            element.checked = false;
          });
        } else {
          this.attachments = null;
        }

      }, err => {
        this.isLoading = false;
        this.attachments = null;
      });
  }

  loadCriteria() {
    this.criteria = [];
    switch (this.calibration.TableName) {
      case 'external_calibrations': {
        this.loadExternalCalibrationCriteria();
        break;
      }
      case 'calibrations': {
        break;
      }
      case 'new_calibrations': {
        this.loadNewCalibrationCriteria();
        break;
      }
    }
  }

  loadExternalCalibrationCriteria() {
    this.isLoading = true;
    let criterionAcceptance: Array<CriterionAcceptance> = new Array<CriterionAcceptance>();
    this.criterionService
      .getFromExternalCalibration(this.calibration.Identifier, this.app.currentContact.Customer.Identifier)
      .subscribe(values => {
        this.isLoading = false;
        criterionAcceptance = values as CriterionAcceptance[];
        if (criterionAcceptance) {
          criterionAcceptance.forEach(cri => {
            this.translate.get('Translate.BandsAmplitude').subscribe((val) => {
              let abbreviation = '%';
              if (cri.Type === EReferenceModeType.AbsoluteValue)
                abbreviation = cri.InstrumentBand ? cri.InstrumentBand.MeasurementUnit.Abbreviation : null;
              cri.CriterionDescription = Helper.getRangeValueFormated(null, cri.MaxErrorLower, cri.MaxErrorMore, cri.InstrumentBand.Precision, abbreviation, val);
            });
            this.fillBand(cri.InstrumentBand);
            this.criteria.push({
              table: cri.Name,
              name: cri.Name,
              band: cri.InstrumentBand.BandDescription,
              type: cri.Type,
              limit: cri.CriterionDescription
            });
          });
        } else {
          this.criteria = null;
        }
      }, err => {
        this.isLoading = false;
        this.criteria = null;
      });
  }

  loadNewCalibrationCriteria() {
    this.isLoading = true;
    let calRegTable: CalRegTable[] = new Array<CalRegTable>();

    this.criterionService
      .getFromCalibration(this.calibration.Identifier)
      .subscribe(values => {
        this.isLoading = false;
        calRegTable = values as CalRegTable[];
        if (calRegTable) {
          calRegTable.forEach(crt => {
            if (crt.Criterions.length > 0) {
              crt.Criterions.forEach(cri => {
                this.translate.get('Translate.BandsAmplitude').subscribe((val) => {
                  let abbreviation = '%';
                  if (cri.Type === EReferenceModeType.AbsoluteValue)
                    abbreviation = crt.Band ? crt.Band.MeasurementUnit.Abbreviation : null;
                  cri.CriterionDescription = Helper.getRangeValueFormated(null, cri.MaxErrorLower, cri.MaxErrorMore, crt.Band.Precision, abbreviation, val);
                });
                this.fillBand(crt.Band);
                this.criteria.push({
                  table: crt.Name,
                  name: cri.Name,
                  band: crt.Band.BandDescription,
                  type: cri.Type,
                  limit: cri.CriterionDescription
                });
              });
            } else {
              this.criteria = null;
            }
          });
        } else {
          this.criteria = null;
        }
      }, err => {
        this.isLoading = false;
        this.criteria = null;
      });
  }

  loadSupplements() {
    this.isLoading = true;
    let lstFilters = '"Calibration.Identifier_=\'' + this.calibration.Identifier + '\'"';

    this.supplementService
      .get(null, null, null, null, null, null, lstFilters, null)
      .subscribe(values => {
        this.isLoading = false;
        this.supplements = values.json();

        if (this.supplements.length > 0) {
          this.supplements.forEach(element => {
            element.checked = false;
          });
        } else {
          this.supplements = null;
        }

      }, err => {
        this.isLoading = false;
        this.supplements = null;
      });
  }

  loadBands() {
    this.isLoading = true;
    this.instrumentService.getbands([this.calibration.Instrument]).subscribe(res => {
      this.isLoading = false;
    });
  }

  onAttachmentSelecionChange() {
    if (this.attachments.filter(item => item.checked).length !== this.attachments.length && this.chkAllAttachments) {
      this.chkAllAttachments = false;
    }
    if (this.attachments.filter(item => item.checked).length === this.attachments.length && !this.chkAllAttachments) {
      this.chkAllAttachments = true;
    }

    this.checkedAttachments = this.attachments.filter(item => item.checked).length;
  }

  onSupplementSelecionChange() {
    if (this.supplements.filter(item => item.checked).length !== this.supplements.length && this.chkAllSupplements) {
      this.chkAllSupplements = false;
    }
    if (this.supplements.filter(item => item.checked).length === this.supplements.length && !this.chkAllSupplements) {
      this.chkAllSupplements = true;
    }

    this.checkedSupplements = this.supplements.filter(item => item.checked).length;
  }

  openAttachment() {
    this.isLoading = true;
    let lst = this.attachments.filter(at => at.checked);
    let file: any[] = new Array<any>();

    let fileName = this.storedFileService.formatUserFileName(lst[0].UserFileName);
    file.push({ identifier: lst[0].Identifier, file: fileName ? fileName : lst[0].SystemFileName, size: this.storedFileService.formatFileSize(lst[0].size), SystemFileName: lst[0].SystemFileName, userFileName: lst[0].UserFileName });

    this.storedFileService.download(file)
      .subscribe(data => {
        if (data) {
          const blob = new Blob([data], { type: data.type });

          if (blob.type === 'application/pdf') {
            this.pdfViewer.pdfSrc = blob;
            this.pdfViewer.refresh();
          } else {
            const fileUrl = URL.createObjectURL(data);
            const newWindow = window.open(fileUrl);

            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              this.dialog.openPopupDialog();
            }
          }

          this.isLoading = false;
        } else {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
          this.isLoading = false;
        }
      }, err => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
      });

  }

  openCustomerStatusDialog(readOnly: boolean) {
    let lst: any[] = [];
    lst[0] = this.calibration;
    this.dialog.customerStatus(lst, readOnly).subscribe((res) => {
      if (res === undefined && !readOnly) {
        this.ngOnInit();
        this.calibrationListComponent.refresh();
      }
    });
  }

  openSupplement() {
    this.isLoading = true;
    let supplement = this.supplements.filter(item => item.checked)[0];
    this.supplementService.view(supplement.Identifier).subscribe(data => {
      this.isLoading = false;
      const fileUrl = URL.createObjectURL(data);
      const newWindow = window.open(fileUrl);

      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        this.dialog.openPopupDialog();
      }
    }, err => {
      this.isLoading = false;
      this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateSupplementError');
    });
  }


  setCustomerStatus() {
    this.openCustomerStatusDialog(false);
  }

  /**
	 * Aciona o evento na mudança de Tab
	 * @param tab md-tab selecionada
	 */
  tabChanged(tab): void {
    if (tab.index === 1) {
      // So carrega os criterios na primeira vez que abre a aba
      if (this.criteria && this.criteria.length > 0) {
        return;
      }
      this.loadCriteria();
    }
  }

  view(download: boolean) {
    this.isLoading = true;
    let lstFilters = '"Calibration.Identifier_in(\'' + this.calibration.Identifier + '\')"';
    let lstFields = '"SystemFileName","Calibration.Id","Calibration.Code","Calibration.Date"';
    let storedFiles: any[] = [];

    this.storedFileService.get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(value => {
        storedFiles = value.json() as StoredFile[];
        if (!storedFiles || storedFiles.length === 0) {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateError', 'info_outline', '#009688');
          this.isLoading = false;
        }
        this.storedFileService.download(storedFiles)
          .subscribe(data => {
            this.isLoading = false;
            if (data) {
              const file = new Blob([data], { type: data.type });

              this.storedFileService.view(file, download, this.pdfViewer, `Certificado ${storedFiles[0].Calibration.Code}`);

            } else {
              this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateError');
              this.isLoading = false;
            }
          }, () => {
            this.dialog.error('Message.ErrorAlert', 'Message.OpenCertificateError');
            this.isLoading = false;
          });
      }, () => {
        this.dialog.error('Message.ErrorAlert', 'Message.OpenCertificateError');
        this.isLoading = false;
      });
  }

  viewCustomerStatus() {
    this.openCustomerStatusDialog(true);
  }
}



// WEBPACK FOOTER //
// ./src/app/calibrations/calibration/calibration.component.ts